import { computed, inject, Injector, runInInjectionContext, Signal } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, Routes, UrlTree } from '@angular/router';
import { PageNotFoundComponent } from '@r1-compass-library/components';
import { TopLevelRoutes } from '@r1-compass-library/models';
import { LocalStorageService } from '@r1-compass-library/services/local-storage.service';
import { LoginCookieService } from '@r1-compass-library/services/login-cookie.service';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { GlobalUsersStore } from '@r1-compass-library/store/users/globalUsersStore';
import { assertInjector } from 'ngxtension/assert-injector';
import { AppConstants } from './components/app.constants';
import { CompassComponent } from './components/compass.component';
import { LoginComponent } from './components/login/login.component';
import { ZodExampleComponent } from './components/test/csv-9934/csv-9934.component';
import { FieldMapperExampleComponent } from './components/test/field-mapper-example/field-mapper-example.component';
import { FroalaSeperateContextComponent } from './components/test/froala-seperate-context.component';
import { InterceptorDataComponent } from './components/test/interceptor-data.component';
import { NgEighteenComponent } from './components/test/ng-eighteen/ng-eighteen.component';
import { RoutingDebugComponent } from './components/test/routing-debug.component';
import { TestComponent } from './components/test/test.component';
import { SwitchDivisonComponent } from './components/login/switch-division/switch-division.component';

// Guards
import {
    hasUnsavedChanges,
    hasUnsavedChangesAllowPartialSave,
} from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { hasUnsavedChangesAdminDivision } from '@r1-compass-library/guards/save-guards/admin-division.guard';
import { hasUnsavedChangesPlayRecordSelection } from '@r1-compass-library/guards/save-guards/play-record-selection.guard';
import { hasUnsavedChangesTemplateCriteria } from '@r1-compass-library/guards/save-guards/template-criteria-builder.guard';
import { hasUnsavedChangesEnableComponent } from '@r1-compass-library/guards/save-guards/enable.guard';

export const routes: Routes = [
    { path: 'index.html', component: LoginComponent },
    { path: 'login', component: LoginComponent, title: 'Login' },
    {
        path: 'switch-division',
        component: SwitchDivisonComponent,
        title: 'Switch Division',
        canActivate: [hasOauthCookie()],
    },
    {
        path: '',
        component: CompassComponent,
        canActivate: [hasDivisionGuard(), hasOauthCookie()],
        canActivateChild: [hasAuthorization(), jwtNotExpired()],
        children: [
            {
                path: AppConstants.PlaysComponent.path,
                component: AppConstants.PlaysComponent.component,
                data: AppConstants.PlaysComponent,
                title: 'Plays',
                children: [
                    {
                        path: AppConstants.PlaysListComponent.path,
                        component: AppConstants.PlaysListComponent.component,
                        data: AppConstants.PlaysListComponent,
                    },
                    {
                        path: ':id',
                        children: [
                            {
                                path: AppConstants.PlayDefinitionComponent.path,
                                component: AppConstants.PlayDefinitionComponent.component,
                                data: AppConstants.PlayDefinitionComponent,
                                canDeactivate: [hasUnsavedChangesAllowPartialSave],
                            },
                            {
                                path: AppConstants.PlayRecordSelectionComponent.path,
                                component: AppConstants.PlayRecordSelectionComponent.component,
                                data: AppConstants.PlayRecordSelectionComponent,
                                canDeactivate: [hasUnsavedChangesPlayRecordSelection],
                            },
                            {
                                path: AppConstants.PlayTalkingPointsComponent.path,
                                component: AppConstants.PlayTalkingPointsComponent.component,
                                data: AppConstants.PlayTalkingPointsComponent,
                                canDeactivate: [hasUnsavedChangesAllowPartialSave],
                            },
                            {
                                path: AppConstants.PlayReportingComponent.path,
                                component: AppConstants.PlayReportingComponent.component,
                                data: AppConstants.PlayReportingComponent,
                            },
                            {
                                path: AppConstants.PlaySchedulingComponent.path,
                                component: AppConstants.PlaySchedulingComponent.component,
                                data: AppConstants.PlaySchedulingComponent,
                                canDeactivate: [hasUnsavedChangesAllowPartialSave],
                            },
                        ],
                    },
                ],
            },
            {
                path: AppConstants.TemplateCriteriaComponent.path,
                component: AppConstants.TemplateCriteriaComponent.component,
                data: AppConstants.TemplateCriteriaComponent,
                title: 'Template Criteria',
                children: [
                    {
                        path: AppConstants.TemplateCriteriaListComponent.path,
                        component: AppConstants.TemplateCriteriaListComponent.component,
                        data: AppConstants.TemplateCriteriaListComponent,
                    },
                    {
                        path: ':id',
                        children: [
                            {
                                path: AppConstants.TemplateCriteriaDefinitionComponent.path,
                                component: AppConstants.TemplateCriteriaDefinitionComponent.component,
                                data: AppConstants.TemplateCriteriaDefinitionComponent,
                                canDeactivate: [hasUnsavedChangesAllowPartialSave],
                            },
                            {
                                path: AppConstants.TemplateCriteriaBuilderComponent.path,
                                component: AppConstants.TemplateCriteriaBuilderComponent.component,
                                data: AppConstants.TemplateCriteriaBuilderComponent,
                                canDeactivate: [hasUnsavedChangesTemplateCriteria],
                            },
                            {
                                path: AppConstants.TemplateCriteriaEnableComponent.path,
                                component: AppConstants.TemplateCriteriaEnableComponent.component,
                                data: AppConstants.TemplateCriteriaEnableComponent,
                                canDeactivate: [hasUnsavedChangesEnableComponent],
                            },
                        ],
                    },
                ],
            },
            {
                path: AppConstants.LifecycleRulesComponent.path,
                component: AppConstants.LifecycleRulesComponent.component,
                data: AppConstants.LifecycleRulesComponent,
                title: 'Lifecycle Rules',
                children: [
                    {
                        path: AppConstants.LifecycleRulesListComponent.path,
                        component: AppConstants.LifecycleRulesListComponent.component,
                        data: AppConstants.LifecycleRulesListComponent,
                    },
                    {
                        path: ':id',
                        children: [
                            {
                                path: AppConstants.LifecycleRulesDefinitionComponent.path,
                                component: AppConstants.LifecycleRulesDefinitionComponent.component,
                                data: AppConstants.LifecycleRulesDefinitionComponent,
                                canDeactivate: [hasUnsavedChangesAllowPartialSave],
                            },
                            {
                                path: AppConstants.LifecycleRulesRecordSelectionComponent.path,
                                component: AppConstants.LifecycleRulesRecordSelectionComponent.component,
                                data: AppConstants.LifecycleRulesRecordSelectionComponent,
                                canDeactivate: [hasUnsavedChangesAllowPartialSave],
                            },
                            {
                                path: AppConstants.LifecycleRulesEnableComponent.path,
                                component: AppConstants.LifecycleRulesEnableComponent.component,
                                data: AppConstants.LifecycleRulesEnableComponent,
                                canDeactivate: [hasUnsavedChangesEnableComponent],
                            },
                        ],
                    },
                ],
            },
            {
                path: AppConstants.ContentLibraryComponent.path,
                component: AppConstants.ContentLibraryComponent.component,
                data: AppConstants.ContentLibraryComponent,
                title: 'Content Library',
                children: [
                    {
                        path: AppConstants.ContentLibraryListComponent.path,
                        component: AppConstants.ContentLibraryListComponent.component,
                        data: AppConstants.ContentLibraryListComponent,
                    },
                    {
                        path: ':assetId',
                        children: [
                            {
                                path: AppConstants.ContentLibraryDefinitionComponent.path,
                                component: AppConstants.ContentLibraryDefinitionComponent.component,
                                data: AppConstants.ContentLibraryDefinitionComponent,
                                canDeactivate: [hasUnsavedChangesAllowPartialSave],
                            },
                            {
                                path: AppConstants.ContentLibraryRulesComponent.path,
                                component: AppConstants.ContentLibraryRulesComponent.component,
                                data: AppConstants.ContentLibraryRulesComponent,
                                canDeactivate: [hasUnsavedChanges],
                            },
                            {
                                path: AppConstants.ContentLibraryEnableComponent.path,
                                component: AppConstants.ContentLibraryEnableComponent.component,
                                data: AppConstants.ContentLibraryEnableComponent,
                                canDeactivate: [hasUnsavedChangesEnableComponent],
                            },
                            {
                                path: AppConstants.ContentLibraryRulesComponent.path + '/new',
                                component: AppConstants.ContentLibraryEditRuleComponent.component,
                                canDeactivate: [hasUnsavedChangesAllowPartialSave],
                            },
                            {
                                path: AppConstants.ContentLibraryRulesComponent.path + '/:ruleId/edit-rule',
                                component: AppConstants.ContentLibraryEditRuleComponent.component,
                                canDeactivate: [hasUnsavedChangesAllowPartialSave],
                            },
                        ],
                    },
                ],
            },
            {
                path: AppConstants.ReportingComponent.path,
                component: AppConstants.ReportingComponent.component,
                data: AppConstants.ReportingComponent,
                title: 'Reporting',
            },
            {
                path: AppConstants.DivisionSettingsComponent.path,
                component: AppConstants.DivisionSettingsComponent.component,
                data: AppConstants.DivisionSettingsComponent,
                title: 'Division Settings',
                children: [
                    {
                        path: ':id',
                        children: [
                            {
                                path: AppConstants.DivisionBasicSettingsComponent.path,
                                component: AppConstants.DivisionBasicSettingsComponent.component,
                                data: AppConstants.DivisionBasicSettingsComponent,
                                canDeactivate: [hasUnsavedChanges],
                            },
                            {
                                path: AppConstants.DivisionSalesforceTargetsComponent.path,
                                component: AppConstants.DivisionSalesforceTargetsComponent.component,
                                data: AppConstants.DivisionSalesforceTargetsComponent,
                                canDeactivate: [hasUnsavedChanges],
                            },
                            {
                                path: AppConstants.DivisionDataMappingComponent.path,
                                component: AppConstants.DivisionDataMappingComponent.component,
                                data: AppConstants.DivisionDataMappingComponent,
                                canDeactivate: [hasUnsavedChanges],
                            },
                            {
                                path: AppConstants.DivisionCriteriaSourceSettingsComponent.path,
                                component: AppConstants.DivisionCriteriaSourceSettingsComponent.component,
                                data: AppConstants.DivisionCriteriaSourceSettingsComponent,
                                canDeactivate: [hasUnsavedChanges],
                            },
                            {
                                path: AppConstants.DivisionScheduleComponent.path,
                                component: AppConstants.DivisionScheduleComponent.component,
                                data: AppConstants.DivisionScheduleComponent,
                                canDeactivate: [hasUnsavedChanges],
                            },
                        ],
                    },
                ],
            },
            {
                path: AppConstants.AdminSettingsComponent.path,
                component: AppConstants.AdminSettingsComponent.component,
                data: AppConstants.AdminSettingsComponent,
                title: 'Admin Settings',
                children: [
                    {
                        path: AppConstants.AdminDivisionsComponent.path,
                        component: AppConstants.AdminDivisionsComponent.component,
                        data: AppConstants.AdminDivisionsComponent,
                        canDeactivate: [hasUnsavedChangesAdminDivision],
                    },
                    {
                        path: AppConstants.AdminUsersComponent.path,
                        component: AppConstants.AdminUsersComponent.component,
                        data: AppConstants.AdminUsersComponent,
                    },
                ],
            },
        ],
    },
    {
        path: 'debug',
        children: [
            { path: 'froala', component: FroalaSeperateContextComponent },
            { path: 'csv-9934', component: ZodExampleComponent },
            { path: 'testing-stuff', component: TestComponent },
            { path: 'field-mapper-example', component: FieldMapperExampleComponent },
            { path: 'ng18', component: NgEighteenComponent },
            {
                path: 'routing-debug',
                children: [
                    {
                        path: '**',
                        component: RoutingDebugComponent,
                    },
                ],
            },
            { path: 'interceptor-data', title: 'Interceptor Debug', component: InterceptorDataComponent },
        ],
    },
    { path: '**', component: PageNotFoundComponent, title: 'Page Not Found' },
];

export function handleBrowserStorageAndMakeLogoutRoute(injector?: Injector, includeReturnUrl = true): UrlTree {
    injector = assertInjector(handleBrowserStorageAndMakeLogoutRoute, injector);

    return runInInjectionContext(injector, () => {
        const router = inject(Router);

        const redirectUrl = router.currentNavigation()?.extractedUrl.toString() ?? router.url;

        inject(LoginCookieService).resetAllCookies();
        inject(LocalStorageService).clear();
        inject(GlobalLoggedInUserStore).resetState();
        inject(GlobalDivisionsStore).resetState();
        inject(GlobalUsersStore).resetState();

        const shouldAttachRedirectUrl =
                includeReturnUrl &&
                redirectUrl &&
                redirectUrl !== '/' &&
                !redirectUrl.startsWith('/login');

        return router.createUrlTree(['login'], {
                queryParams: shouldAttachRedirectUrl ? { returnUrl: redirectUrl } : undefined,
            });
    });
}

function hasDivisionGuard(): CanActivateFn {
    return () => {
        const globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
        const hasDivisionId: Signal<boolean> = computed(() => {
            const divisionId = globalLoggedInUserStore.selectedIamDivisionId();
            return divisionId !== undefined;
        });
        const loginCookieService = inject(LoginCookieService);
        return (
            loginCookieService.hasDivTokenCookie() ||
            globalLoggedInUserStore.usersAvailableDivisions().length > 0 ||
            hasDivisionId() ||
            handleBrowserStorageAndMakeLogoutRoute()
        );
    };
}

function hasOauthCookie(): CanActivateFn {
    return () => {
        const loginCookieService = inject(LoginCookieService);
        return loginCookieService.hasJWTCookie() || handleBrowserStorageAndMakeLogoutRoute();
    };
}

function jwtNotExpired(): CanActivateFn {
    return () => {
        const localStorageService = inject(LocalStorageService);

        const expiration = localStorageService.jwtClaims()?.expiration;
        const now = Date.now();

        if (expiration) {
            const notExpired = now < expiration;

            return notExpired || handleBrowserStorageAndMakeLogoutRoute();
        } else {
            return handleBrowserStorageAndMakeLogoutRoute();
        }
    };
}

function hasAuthorization(): CanActivateFn {
    return (route: ActivatedRouteSnapshot, rss: RouterStateSnapshot) => {
        const router = inject(Router);

        const matchingTopLevelRoute = TopLevelRoutes.find(topRoute => rss.url.includes(topRoute.path));
        const jwtPaths =
            inject(LocalStorageService)
                .jwtClaims()
                ?.recs.map(rec => rec.path) ?? [];
        const destinationRouteInJwt = jwtPaths.find(rec => rec === matchingTopLevelRoute?.claim);

        const redirectTo = jwtPaths.includes('/play*')
            ? router.createUrlTree(['plays'], { queryParams: { folderId: '0' } })
            : handleBrowserStorageAndMakeLogoutRoute();

        return destinationRouteInJwt !== undefined || redirectTo;
    };
}
